import { SEED_KERNVOKABULAR, SEED_PHRASES } from '../../data/seed/content';
import { ALPHABET_GROUPS } from '../../data/seed/alphabetGroups';
import { BODY_REGIONS, PAIN_LEVEL_WORDS, type BodyRegion } from '../Notfall/notfallData';

/**
 * What the printed kit contains, assembled from the app's own seed data.
 *
 * ADR-007: the no-tech layer sits *below* the software, and its whole value is that it keeps working
 * when the software does not — a flat battery, a lost iPad, an afternoon when the screen is too
 * much. That only holds if the paper says the same things the screen says. A card with a different
 * vocabulary, or an alphabet card whose groups run in a different order, would teach a partner a
 * sequence the app does not follow, and they would find that out on the day it mattered.
 *
 * So nothing here is authored. Every string is pulled from data that already ships and is already
 * tested, and `tests/unit/kit.test.ts` asserts the identity rather than trusting it.
 */

export interface KitBodySheet {
  side: 'front' | 'back';
  label: string;
  regions: BodyRegion[];
}

/** The six sheets, in printing order. */
export const KIT_SHEETS = ['core', 'notfall', 'pain', 'alphabet', 'partner', 'backup'] as const;
export type KitSheetId = (typeof KIT_SHEETS)[number];

/** Kernvokabular for the core board — the ~40 words that carry most of everyday speech (§5.3). */
export const kitCoreWords = (): readonly string[] => SEED_KERNVOKABULAR;

/**
 * The Notfall core set, from the seeded phrases rather than a copy.
 *
 * These are the five the app protects from deletion, so the card and the screen cannot diverge even
 * if a caregiver reorganises everything else.
 */
export const kitNotfallPhrases = (): Array<{ label: string; text: string }> =>
  SEED_PHRASES.filter((phrase) => phrase.categoryId === 'cat-notfall').map((phrase) => ({
    label: phrase.shortLabel ?? phrase.text,
    text: phrase.text,
  }));

/** 0–10 with the German words, the same table the app speaks from. */
export const kitPainLevels = (): Array<{ level: number; word: string }> =>
  PAIN_LEVEL_WORDS.map((word, level) => ({ level, word }));

/**
 * Body regions, grouped by side.
 *
 * The printed card carries the **figure** the screen could not: on A4 its regions come out around
 * 40 × 31 mm, where on screen a standing figure allowed only 1.9 horizontal zones at the 16 mm touch
 * floor. Pointing at your own body needs no reading, and this card is used exactly when reading is
 * hardest — so the design's original body-map instinct was right, for paper.
 *
 * The region *names* and the sentences behind them are the app's, so the vocabulary matches even
 * though the presentation differs.
 */
export const kitBodySheets = (): KitBodySheet[] => [
  { side: 'front', label: 'Vorne', regions: BODY_REGIONS.filter((r) => r.side === 'front') },
  { side: 'back', label: 'Hinten', regions: BODY_REGIONS.filter((r) => r.side === 'back') },
];

/** The alphabet groups, in the order the partner-assisted surface asks them. */
export const kitAlphabetGroups = (): typeof ALPHABET_GROUPS => ALPHABET_GROUPS;

/**
 * The partner instruction sheet.
 *
 * The sheet most likely to be skipped, and the one that changes most: it is the difference between a
 * partner who waits and a partner who guesses. Finishing someone's sentence for them is meant kindly
 * and takes the conversation away from them, so it is said plainly here rather than implied.
 *
 * Data rather than JSX so the wording is reviewable in one place by a caregiver — the same reason
 * the UI strings live in `i18n/de.ts` (SUR-18).
 */
export interface KitInstructionSection {
  heading: string;
  points: string[];
}

export const KIT_INSTRUCTIONS: readonly KitInstructionSection[] = [
  {
    heading: 'Warten',
    points: [
      'Tippen dauert. Ein Satz kann eine Minute brauchen — das ist normal und kein Zeichen von Verwirrung.',
      'Bitte nicht den Satz zu Ende sprechen, auch wenn Sie ahnen, wie er weitergeht. Es ist freundlich gemeint und nimmt das Gespräch aus der Hand.',
      'Bitte keine Ja-Nein-Fragen stellen, solange getippt wird. Die Antwort auf eine andere Frage geht sonst verloren.',
      'Wenn «ich schreibe…» auf dem Bildschirm steht, wird gerade geschrieben. Bitte abwarten.',
    ],
  },
  {
    heading: 'Sprechen lassen',
    points: [
      'Die Schnellwörter unten — Ja, Nein, Warte, Stopp, Aua, Danke — sind mit einem Antippen gesprochen. Sie sind immer an derselben Stelle.',
      '«Stopp» heisst: bitte jetzt aufhören zu sprechen. Das Gerät sagt manchmal etwas Falsches, weil eine Taste danebengegangen ist. Das ist kein Versehen der Person, sondern ein Fehlgriff.',
      'Der grosse Text oben kann angetippt werden, dann erscheint er bildschirmfüllend — zum Herzeigen, wenn der Ton nicht reicht.',
    ],
  },
  {
    heading: 'Die Buchstabenkarte',
    points: [
      'Wenn Tippen nicht geht: Sie lesen eine Gruppe laut vor — «E, N, I, S, R?» — und warten auf Ja oder Nein.',
      'Bei Ja gehen Sie die Buchstaben der Gruppe einzeln durch. Bei Nein nehmen Sie die nächste Gruppe.',
      'Ein falsches Ja kostet nur eine Frage. Gehen Sie einfach zurück zu den Gruppen.',
      'Die Reihenfolge auf dieser Karte ist genau die des Geräts. Sie können beides mischen.',
    ],
  },
  {
    heading: 'Wenn nichts geht',
    points: [
      'Die Kernwörter-Tafel und die Notfallkarte funktionieren ohne Strom, ohne Netz und ohne Gerät.',
      'Zeigen lassen ist immer erlaubt. Auch auf den Körper, wenn es um Schmerzen geht.',
      'Der Rufknopf im Zimmer ist ein Knopf, kein Programm. Das Gerät ersetzt ihn nicht.',
    ],
  },
];

/**
 * The restore procedure, in plain German — kit sheet 6 (4.8).
 *
 * **This is the single source.** The same strings are rendered onto the printed sheet and asserted to appear
 * in `docs/restore-runbook.md`, so the card in the drawer and the procedure in the repository cannot drift
 * apart. A restore procedure that is subtly wrong is worse than none, because somebody follows it under
 * pressure and concludes the backup is gone.
 *
 * Written for the worst realistic moment: a replacement iPad, an upset relative, and a laminated card. Numbered
 * because that is what somebody can hold a finger on while typing with the other hand.
 */
export const KIT_RESTORE_STEPS: readonly string[] = [
  "Sag's auf dem neuen iPad öffnen und über «Teilen» zum Home-Bildschirm hinzufügen. Nicht im Safari-Tab lassen.",
  'Beim ersten Start erscheint oben «Dieses Gerät ist neu. Backup wiederherstellen?» — antippen. Sonst: oben rechts zwei Sekunden lang gedrückt halten, dann «Backup». Geht das Halten nicht: oben rechts fünf Mal kurz antippen.',
  'Bei «Ich habe schon ein Backup» den Geräteschlüssel von dieser Karte eintragen und «Übernehmen».',
  '«Aus der Cloud wiederherstellen» antippen. Es erscheint eine Liste mit Datum — den neuesten Eintrag wählen.',
  'Die sechs Wörter von dieser Karte eintragen und bestätigen. Das dauert einen Moment; das ist normal.',
  'Wenn es fertig ist: «Ton und Stimme» → «Audio vorbereiten», mit WLAN. Sonst spricht das Gerät nur mit Netz.',
  'Zum Schluss prüfen, dass die Sätze und die Notfallkarte wieder da sind.',
];

/** What goes on the card by hand, never printed by the app. */
export const KIT_SECRET_LINES: readonly string[] = ['Sechs Wörter', 'Geräteschlüssel'];
